import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  TablePagination,
  InputAdornment,
  Switch
} from '@mui/material';
import TextField from '../components/SearchableTextField';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyOffIcon from '@mui/icons-material/KeyOff';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useTranslation } from 'react-i18next';
import { localizedErrorMessage } from '../utils/italianErrorMessage';

import TopBar from '../components/TopBar.js';

const formatDateTime24 = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

// --- tiny helper: fetch with auth token from localStorage ---
async function apiFetch(path, { method = 'GET', headers = {}, body } = {}) {
  const token = localStorage.getItem('accessToken');
  const res = await fetch(path, {
    method,
    headers: {
      'Authorization': token ? `Bearer ${token}` : undefined,
      ...(body && typeof body !== 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body && typeof body !== 'string' ? JSON.stringify(body) : body,
  });
  if (!res.ok) {
    const msg = await res.text();
    throw new Error(`${res.status} ${res.statusText} — ${msg}`);
  }
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}

// ----------------------------
// TableHeader Component
// ----------------------------
function TableHeader({ field, label, sort, onSort, style = {} }) {
  const isActive = sort.field === field;
  const isAsc = isActive && sort.direction === 'asc';
  return (
    <th
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative',
        paddingRight: '24px',
        ...style
      }}
      onClick={() => onSort(field)}
    >
      {label}
      {isActive && (
        <Box component="span" sx={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}>
          {isAsc ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
        </Box>
      )}
    </th>
  );
}

// ----------------------------
// Create Tenant Dialog
// ----------------------------
function CreateTenantDialog({ open, onClose, onCreate }) {
  const { t } = useTranslation();
  const [tenantId, setTenantId] = useState('');
  const [tenantType, setTenantType] = useState(''); // Tenant type:Spa,ristorante,sala Congeresi
  const valid = tenantId.trim() && tenantType.trim();
  return (
    <Dialog open={open} onClose={() => {
      setTenantId('')
      setTenantType('')
      onClose()
    }} fullWidth maxWidth="sm">
      <DialogTitle>{t('admin.tenants.dialogs.create.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label={t('admin.tenants.dialogs.create.label')}
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            inputProps={{ style: { textTransform: 'lowercase' } }}
          />
          <TextField
            select
            label={t('admin.tenants.table.tenantType')}
            value={tenantType}
            onChange={(e) => setTenantType(e.target.value)}
          >
            <MenuItem value="RISTORANTE">{t('systemSettings.tenantTypes.RISTORANTE', { defaultValue: 'Restaurant' })}</MenuItem>
            <MenuItem value="SPA">{t('systemSettings.tenantTypes.SPA', { defaultValue: 'Spa' })}</MenuItem>
            <MenuItem value="SALE_CONGRESSI">{t('systemSettings.tenantTypes.SALE_CONGRESSI', { defaultValue: 'Conference rooms' })}</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setTenantId('')
          setTenantType('')
          onClose()
        }}>{t('common.actions.cancel')}</Button>
        <Button disabled={!valid} variant="contained" onClick={() => onCreate({ tenantId, tenantType })}>
          {t('common.actions.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ----------------------------
// Create User Dialog
// ----------------------------
function CreateUserDialog({ open, onClose, onCreate, role, selectedTenant }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    surname: '',
    email: '',
    password: '',
    tenantId: selectedTenant?.tenantId || ''
  });

  useEffect(() => {
    if (!open) {
      setForm({ name: '', surname: '', email: '', password: '', tenantId: selectedTenant?.tenantId || '' });
    } else {
      setForm(prev => ({ ...prev, tenantId: selectedTenant?.tenantId || '' }));
    }
  }, [open, selectedTenant]);

  const can = form.name && form.surname && form.email && form.password;

  function resetAll() {
    setForm({
      name: '',
      surname: '',
      email: '',
      password: '',
      tenantId: selectedTenant?.tenantId || ''
    })
  };

  return (
    <Dialog open={open} onClose={() => {
        resetAll()  
        onClose()
      }} fullWidth maxWidth="sm"
    >
      <DialogTitle>
        {role === 'superadmin'
          ? t('admin.users.dialogs.createUser.titleSuperadmin')
          : t('admin.users.dialogs.createUser.titleAdmin')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label={t('admin.users.dialogs.createUser.fields.name')} 
            value={form.name} 
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField label={t('admin.users.dialogs.createUser.fields.surname')} 
            value={form.surname} 
            onChange={(e) => setForm({ ...form, surname: e.target.value })} />
          <TextField label={t('admin.users.dialogs.createUser.fields.email')} 
            value={form.email} 
            onChange={(e) => setForm({ ...form, email: e.target.value })} 
            autoComplete="new-email" />
          <TextField type="password" label={t('admin.users.dialogs.createUser.fields.password')} 
            value={form.password} 
            onChange={(e) => setForm({ ...form, password: e.target.value })} 
            autoComplete="new-password" />
          
          {role === 'admin' && selectedTenant && (
            <TextField
              label={t('admin.users.dialogs.createUser.fields.tenant')}
              value={`${selectedTenant.Id ?? selectedTenant.id} (${selectedTenant.tenantId})`}
              InputProps={{ readOnly: true }}
              variant="filled"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button disabled={!can} variant="contained" onClick={() => onCreate(form)}>
          {t('common.actions.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ----------------------------
// Empty / NoResults helper
// ----------------------------
function EmptyState({ title, subtitle, action }) {
  return (
    <Box py={6} textAlign="center">
      <Typography variant="h6" gutterBottom>{title}</Typography>
      {subtitle && <Typography color="text.secondary" sx={{ mb: 2 }}>{subtitle}</Typography>}
      {action}
    </Box>
  );
}

// ----------------------------
// Delete Tenant Dialog  (NEW)
// ----------------------------
function DeleteTenantDialog({ open, onClose, onConfirm, tenantId, loading, error }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');

  useEffect(() => { if (!open) setPassword(''); }, [open]);

  const canSubmit = (password || '').length >= 6; // semplice guard
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('admin.tenants.dialogs.delete.title')}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          {t('admin.tenants.dialogs.delete.subtitle', { id: tenantId })}
        </Typography>
        <TextField
          fullWidth
          autoFocus
          type="password"
          label={t('admin.tenants.dialogs.delete.passwordLabel')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!canSubmit || loading}
          onClick={() => onConfirm(password)}
        >
          {loading ? <CircularProgress size={18} /> : t('common.actions.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CreateIntegrationKeyDialog({ open, tenant, loading, error, result, onClose, onCreate, onCopy }) {
  const { t } = useTranslation();
  const [label, setLabel] = useState('BI');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    if (open) {
      setLabel('BI');
      setExpiresAt('');
    }
  }, [open, tenant?.tenantId]);

  const canCreate = label.trim() && !loading && !result;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('admin.integrationKeys.createTitle', { defaultValue: 'Create integration API key' })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            {t('admin.integrationKeys.tenantHelp', {
              tenantId: tenant?.tenantId || '-',
              defaultValue: `Tenant: ${tenant?.tenantId || '-'}`,
            })}
          </Typography>

          {error ? <Alert severity="error">{error}</Alert> : null}

          {!result ? (
            <>
              <TextField
                label={t('admin.integrationKeys.label', { defaultValue: 'Label' })}
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                inputProps={{ maxLength: 100 }}
                helperText={t('admin.integrationKeys.labelHelp', {
                  defaultValue: 'Example: BI, PowerBI, external report.',
                })}
              />
              <TextField
                type="datetime-local"
                label={t('admin.integrationKeys.expiresAt', { defaultValue: 'Expires at' })}
                value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={t('admin.integrationKeys.expiresAtHelp', {
                  defaultValue: 'Optional. Leave empty for no expiration.',
                })}
              />
            </>
          ) : (
            <>
              <Alert severity="warning">
                {result.warning || t('admin.integrationKeys.storeOnce', {
                  defaultValue: 'Store this key now; it will not be shown again.',
                })}
              </Alert>
              <TextField
                label={t('admin.integrationKeys.keyId', { defaultValue: 'Key ID' })}
                value={result.id || ''}
                InputProps={{ readOnly: true }}
              />
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={() => onCopy(String(result.id || ''))}
              >
                {t('admin.integrationKeys.copyKeyId', { defaultValue: 'Copy key ID' })}
              </Button>
              <TextField
                label={t('admin.integrationKeys.apiKey', { defaultValue: 'API key' })}
                value={result.key || ''}
                multiline
                minRows={2}
                InputProps={{ readOnly: true }}
              />
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={() => onCopy(result.key)}
              >
                {t('common.actions.copy', { defaultValue: 'Copy' })}
              </Button>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {result ? t('common.actions.close', { defaultValue: 'Close' }) : t('common.actions.cancel')}
        </Button>
        {!result ? (
          <Button
            variant="contained"
            disabled={!canCreate}
            onClick={() => onCreate({
              label: label.trim(),
              expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
            })}
          >
            {loading
              ? t('common.actions.saving', { defaultValue: 'Saving...' })
              : t('admin.integrationKeys.create', { defaultValue: 'Create key' })}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

function RevokeIntegrationKeyDialog({ open, tenant, loading, error, onClose, onRevoke }) {
  const { t } = useTranslation();
  const [keyId, setKeyId] = useState('');

  useEffect(() => {
    if (open) setKeyId('');
  }, [open, tenant?.tenantId]);

  const normalizedKeyId = keyId.trim();
  const keyIdNumber = Number(normalizedKeyId);
  const keyIdInvalid = Boolean(normalizedKeyId) && (
    !/^\d+$/.test(normalizedKeyId) ||
    !Number.isSafeInteger(keyIdNumber) ||
    keyIdNumber <= 0
  );
  const helperText = keyIdInvalid
    ? t('admin.integrationKeys.keyIdInvalid', {
      defaultValue: 'Enter the numeric key ID shown after creation, not the API key.',
    })
    : t('admin.integrationKeys.keyIdHelp', {
      defaultValue: 'Use the numeric ID, not the rpt_ API key.',
    });

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {t('admin.integrationKeys.revokeTitle', { defaultValue: 'Revoke integration API key' })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            {t('admin.integrationKeys.revokeHelp', {
              tenantId: tenant?.tenantId || '-',
              defaultValue: `Enter the numeric key id to revoke for tenant ${tenant?.tenantId || '-'}. Do not paste the API key.`,
            })}
          </Typography>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label={t('admin.integrationKeys.keyId', { defaultValue: 'Key ID' })}
            value={keyId}
            onChange={(event) => setKeyId(event.target.value)}
            error={keyIdInvalid}
            helperText={helperText}
            autoFocus
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{t('common.actions.cancel')}</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!normalizedKeyId || keyIdInvalid || loading}
          onClick={() => onRevoke(normalizedKeyId)}
        >
          {loading
            ? t('common.actions.saving', { defaultValue: 'Saving...' })
            : t('admin.integrationKeys.revoke', { defaultValue: 'Revoke key' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

    // ----------------------------
    // Edit Tenant Dialog  NEW
    // ----------------------------
    function EditTenantDialog({ open, onClose, tenant, onSave }) {
  const { t } = useTranslation();
  const [tenantType, setTenantType] = useState('');

  useEffect(() => {
    if (open && tenant) {
      setTenantType(tenant.tenantType || '');
    }
  }, [open, tenant]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('admin.tenants.dialogs.edit.title')}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label={t('admin.tenants.table.tenantId')}
            value={tenant?.tenantId || ''}
            InputProps={{ readOnly: true }}
            variant="filled"
            
          />

          <TextField
            fullWidth
            select
            label={t('admin.tenants.table.tenantType')}
            value={tenantType}
            onChange={(e) => setTenantType(e.target.value)}
            
          >
            <MenuItem value="RISTORANTE">{t('systemSettings.tenantTypes.RISTORANTE', { defaultValue: 'Restaurant' })}</MenuItem>
            <MenuItem value="SPA">{t('systemSettings.tenantTypes.SPA', { defaultValue: 'Spa' })}</MenuItem>
            <MenuItem value="SALE_CONGRESSI">{t('systemSettings.tenantTypes.SALE_CONGRESSI', { defaultValue: 'Conference rooms' })}</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!tenantType}
          onClick={() => onSave({ ...tenant, tenantType })}
        >
          {t('common.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ============================
// MAIN PAGE
// ============================
export default function AdminPage() {
  const { t, i18n } = useTranslation(); // niente namespace con i due punti

  // TENANTS
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [tenantsError, setTenantsError] = useState(null);
  const [openCreateTenant, setOpenCreateTenant] = useState(false);
  const [openEditTenant, setOpenEditTenant] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);

  // USERS
  const [tab, setTab] = useState(0); // 0 superadmin, 1 admin per-tenant
  const [superadmins, setSuperadmins] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [openCreateUser, setOpenCreateUser] = useState(false);

  // UI feedback
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnack({
    open: true,
    message: ['error', 'warning'].includes(severity) ? localizedErrorMessage(message) : message,
    severity,
  });

  // SMTP defaults copied into newly created tenants by the backend
  const [smtpDraft, setSmtpDraft] = useState({
    indirizzoEmail: '',
    userLogin: '',
    passwordLogin: '',
    serverSmtp: '',
    portaSmtp: '',
    hasSmtpPassword: false,
  });
  const [loadingSmtp, setLoadingSmtp] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);

  // Ricerca/paginazione/sort - Tenants
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantPage, setTenantPage] = useState(0);
  const [tenantRowsPerPage, setTenantRowsPerPage] = useState(10);
  const [tenantSort, setTenantSort] = useState({ field: 'tenantId', direction: 'asc' });

  // Ricerca/paginazione/sort - Superadmins
  const [superadminSearch, setSuperadminSearch] = useState('');
  const [superadminPage, setSuperadminPage] = useState(0);
  const [superadminRowsPerPage, setSuperadminRowsPerPage] = useState(10);
  const [superadminSort, setSuperadminSort] = useState({ field: 'name', direction: 'asc' });

  // Ricerca/paginazione/sort - Admins
  const [adminSearch, setAdminSearch] = useState('');
  const [adminPage, setAdminPage] = useState(0);
  const [adminRowsPerPage, setAdminRowsPerPage] = useState(10);
  const [adminSort, setAdminSort] = useState({ field: 'name', direction: 'asc' });

  // Dialog eliminazione con password  (NEW)
  const [deleteDlg, setDeleteDlg] = useState({ open: false, row: null, loading: false, error: '' });
  const [integrationKeyDlg, setIntegrationKeyDlg] = useState({
    open: false,
    row: null,
    loading: false,
    error: '',
    result: null,
  });
  const [revokeIntegrationKeyDlg, setRevokeIntegrationKeyDlg] = useState({
    open: false,
    row: null,
    loading: false,
    error: '',
  });

  const openDeleteTenant = (row) => {
    setDeleteDlg({ open: true, row, loading: false, error: '' });
  };

  const closeDeleteTenant = () => {
    setDeleteDlg({ open: false, row: null, loading: false, error: '' });
  };

  const openCreateIntegrationKey = (row) => {
    setIntegrationKeyDlg({ open: true, row, loading: false, error: '', result: null });
  };

  const closeCreateIntegrationKey = () => {
    setIntegrationKeyDlg({ open: false, row: null, loading: false, error: '', result: null });
  };

  const openRevokeIntegrationKey = (row) => {
    setRevokeIntegrationKeyDlg({ open: true, row, loading: false, error: '' });
  };

  const closeRevokeIntegrationKey = () => {
    setRevokeIntegrationKeyDlg({ open: false, row: null, loading: false, error: '' });
  };

  const createIntegrationKey = async (payload) => {
    const row = integrationKeyDlg.row;
    if (!row?.tenantId) return;

    setIntegrationKeyDlg((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const body = { label: payload.label };
      if (payload.expiresAt) body.expiresAt = payload.expiresAt;
      const tenantPathId = encodeURIComponent(row.tenantId);
      const result = await apiFetch(`/api/tenants/${tenantPathId}/integration-keys`, {
        method: 'POST',
        body,
      });
      setIntegrationKeyDlg((prev) => ({ ...prev, loading: false, result }));
      notify(t('admin.integrationKeys.created', { defaultValue: 'Integration API key created.' }));
    } catch (e) {
      setIntegrationKeyDlg((prev) => ({
        ...prev,
        loading: false,
        error: e?.message || t('common.errors.updateFailed'),
      }));
    }
  };

  const revokeIntegrationKey = async (keyId) => {
    const row = revokeIntegrationKeyDlg.row;
    if (!row?.tenantId) return;

    setRevokeIntegrationKeyDlg((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const tenantPathId = encodeURIComponent(row.tenantId);
      const keyPathId = encodeURIComponent(keyId);
      const result = await apiFetch(`/api/tenants/${tenantPathId}/integration-keys/${keyPathId}`, {
        method: 'DELETE',
      });
      closeRevokeIntegrationKey();
      notify(
        result?.alreadyRevoked
          ? t('admin.integrationKeys.alreadyRevoked', { defaultValue: 'Integration API key was already revoked.' })
          : t('admin.integrationKeys.revoked', { defaultValue: 'Integration API key revoked.' })
      );
    } catch (e) {
      setRevokeIntegrationKeyDlg((prev) => ({
        ...prev,
        loading: false,
        error: e?.message || t('common.errors.updateFailed'),
      }));
    }
  };

  const copyIntegrationKey = async (key) => {
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      notify(t('admin.integrationKeys.copied', { defaultValue: 'API key copied.' }));
    } catch (e) {
      notify(t('admin.integrationKeys.copyFailed', { defaultValue: 'Could not copy the API key.' }), 'error');
    }
  };

  // Sostituisce la vecchia deleteTenant: ora richiede password
  const confirmDeleteTenant = async (password) => {
    const row = deleteDlg.row;
    if (!row) return;

    setDeleteDlg((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      // NB: usiamo body in DELETE; Express la gestisce se il body-parser è configurato.
      await apiFetch(`/api/tenants/${row.id}`, { method: 'DELETE', body: { password } });

      notify(t('admin.tenants.notifications.deleted'));
      closeDeleteTenant();
      await loadTenants();
    } catch (e) {
      setDeleteDlg((prev) => ({
        ...prev,
        loading: false,
        error: e?.message || t('common.errors.updateFailed'),
      }));
    }
  };


  // Helpers
  const sortData = (data, sortConfig) => {
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };
  const filterData = (data, searchTerm, searchFields) => {
    if (!searchTerm) return data;
    return data.filter(item =>
      searchFields.some(field =>
        item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };
  const handleSort = (field, currentSort, setSortFunc) => {
    const direction = currentSort.field === field && currentSort.direction === 'asc' ? 'desc' : 'asc';
    setSortFunc({ field, direction });
  };

  const mapSmtpSettings = (data = {}) => ({
    indirizzoEmail: data.indirizzoEmail || '',
    userLogin: data.userLogin || '',
    passwordLogin: '',
    serverSmtp: data.serverSmtp || '',
    portaSmtp: data.portaSmtp ? String(data.portaSmtp) : '',
    hasSmtpPassword: Boolean(data.hasSmtpPassword),
  });

  const loadSmtpConfig = async () => {
    setLoadingSmtp(true);
    try {
      const data = await apiFetch('/api/admin/smtp-config');
      setSmtpDraft(mapSmtpSettings(data));
    } catch (e) {
      notify(e.message || t('common.errors.loading'), 'error');
    } finally {
      setLoadingSmtp(false);
    }
  };

  const saveSmtpConfig = async () => {
    const indirizzoEmail = (smtpDraft.indirizzoEmail || '').trim();
    const userLogin = (smtpDraft.userLogin || '').trim();
    const passwordLogin = (smtpDraft.passwordLogin || '').trim();
    const serverSmtp = (smtpDraft.serverSmtp || '').trim();
    const portaSmtp = String(smtpDraft.portaSmtp || '').trim();

    if (indirizzoEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(indirizzoEmail)) {
      notify(t('systemSettings.validation.emailInvalid', { defaultValue: 'Email non valida.' }), 'warning');
      return;
    }

    if (portaSmtp) {
      const parsedPort = Number(portaSmtp);
      if (!Number.isInteger(parsedPort) || parsedPort < 1 || parsedPort > 65535) {
        notify(t('systemSettings.smtp.portInvalid', { defaultValue: 'PORTA_SMTP must be between 1 and 65535.' }), 'warning');
        return;
      }
    }

    const payload = {
      indirizzoEmail: indirizzoEmail || null,
      userLogin: userLogin || null,
      serverSmtp: serverSmtp || null,
      portaSmtp: portaSmtp ? Number(portaSmtp) : null,
    };
    if (passwordLogin) payload.passwordLogin = passwordLogin;

    setSavingSmtp(true);
    try {
      const updated = await apiFetch('/api/admin/smtp-config', { method: 'PUT', body: payload });
      setSmtpDraft(mapSmtpSettings(updated));
      notify(t('systemSettings.smtp.saveSuccess', { defaultValue: 'Configurazione SMTP salvata con successo.' }));
    } catch (e) {
      notify(e.message || t('common.errors.updateFailed'), 'error');
    } finally {
      setSavingSmtp(false);
    }
  };

  // ---- API: Tenants ----
  const loadTenants = async () => {
    setLoadingTenants(true);
    setTenantsError(null);
    try {
      const data = await apiFetch('/api/tenants');
      setTenants(data);
      if (data?.length && !selectedTenant) setSelectedTenant(data[0].tenantId);
    } catch (e) {
      setTenantsError(localizedErrorMessage(e, t('common.errors.loading')));
    } finally {
      setLoadingTenants(false);
    }
  };

  const createTenant = async (payload) => {
    await apiFetch('/api/tenants', { method: 'POST', body: payload });
    notify(t('admin.tenants.notifications.created'));
    await loadTenants();
  };
  const updateTenant = async (updatedTenant) => {
    if (!updatedTenant?.tenantId) {
    notify('Missing tenantId', 'error');
    return;
  }
   await apiFetch(`/api/tenants/${updatedTenant.tenantId}/type` ,
    { method: 'PATCH',
      body: { tenantType: updatedTenant.tenantType }
    }
   )
  notify('Tenant updated');
  await loadTenants();
  };

  // Toggle via SWITCH (optimistic)
  const setTenantActiveLocal = (tenantId, isActive) => {
    setTenants(prev => prev.map(t => t.tenantId === tenantId ? { ...t, isActive } : t));
  };
  const toggleTenantSwitch = async (row, checked) => {
    const desired = checked;
    const prev = row.isActive;
    // optimistic
    setTenantActiveLocal(row.tenantId, desired);
    try {
      if (desired) {
        await apiFetch(`/api/tenants/${row.tenantId}/activate`, { method: 'PATCH' });
        notify(t('admin.tenants.notifications.activated', { id: row.tenantId }));
      } else {
        await apiFetch(`/api/tenants/${row.tenantId}/deactivate`, { method: 'PATCH' });
        notify(t('admin.tenants.notifications.deactivated', { id: row.tenantId }));
      }
    } catch (e) {
      setTenantActiveLocal(row.tenantId, prev);
      notify(e.message || t('common.errors.updateFailed'), 'error');
    }
  };

  // ---- API: Users ----
  const loadSuperadmins = async () => {
    const data = await apiFetch('/api/admin/all-superadmins');
    setSuperadmins(data);
  };
  const createSuperadmin = async (form) => {
    const payload = { ...form, language: i18n.language, theme: 'light' };
    await apiFetch('/api/admin/create-superadmin', { method: 'POST', body: payload });
    notify(t('admin.users.notifications.superadminCreated'));
    await loadSuperadmins();
  };
  const deleteSuperadmin = async (u) => {
    if (!window.confirm(t('admin.users.confirm.deleteUser', { email: u.email }))) return;
    await apiFetch(`/api/admin/delete-superadmin/${u.id}`, { method: 'DELETE' });
    notify(t('admin.users.notifications.superadminDeleted'));
    await loadSuperadmins();
  };
  const loadAdmins = async (tenantId) => {
    if (!tenantId) return;
    const data = await apiFetch(`/api/admin/all-admins/${tenantId}`);
    setAdmins(data);
  };
  const createAdmin = async (form) => {
    await apiFetch(`/api/admin/create-admin/${selectedTenant}`, { method: 'POST', body: form });
    notify(t('admin.users.notifications.adminCreated'));
    await loadAdmins(selectedTenant);
  };
  const deleteAdmin = async (tenantId, u) => {
    if (!window.confirm(t('admin.users.confirm.deleteUser', { email: u.email }))) return;
    await apiFetch(`/api/admin/delete-admin/${tenantId}/${u.id}`, { method: 'DELETE' });
    notify(t('admin.users.notifications.adminDeleted'));
    await loadAdmins(tenantId);
  };

  // ---- Effects ----
  useEffect(() => {
    (async () => {
      try {
        await loadTenants();
        await loadSuperadmins();
        await loadSmtpConfig();
      } catch (e) {
        notify(e.message || t('common.errors.loading'), 'error');
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (selectedTenant) loadAdmins(selectedTenant).catch((e) => notify(e.message, 'error')); }, [selectedTenant]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setSuperadminPage(0); setAdminPage(0); }, [tab]);

  // --------- RENDER ----------
  const filteredTenantsAll = useMemo(
    () => sortData(filterData(tenants, tenantSearch, ['tenantId', 'id', 'tenantType']), tenantSort),
    [tenants, tenantSearch, tenantSort]
  );
  const paginatedTenants = useMemo(
    () => filteredTenantsAll.slice(tenantPage * tenantRowsPerPage, tenantPage * tenantRowsPerPage + tenantRowsPerPage),
    [filteredTenantsAll, tenantPage, tenantRowsPerPage]
  );

  return (
    <Box className="admin-responsive-page">
      <TopBar />

      <Box sx={{ p: { xs: 1.5, md: 3 }, minWidth: 0 }}>
        <Card className="rounded-2xl shadow-md" sx={{ mb: 4 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} mb={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <EmailOutlinedIcon color="primary" />
                <Box>
                  <Typography variant="h5">
                    {t('admin.smtp.title', { defaultValue: 'SMTP defaults' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('admin.smtp.help', {
                      defaultValue: 'These values are copied into new tenants when the backend creates them. Tenants can change their own settings later.',
                    })}
                  </Typography>
                </Box>
              </Stack>
              <Button variant="outlined" onClick={loadSmtpConfig} disabled={loadingSmtp || savingSmtp}>
                {loadingSmtp ? <CircularProgress size={18} /> : t('common.actions.refresh', { defaultValue: 'Refresh' })}
              </Button>
            </Stack>

            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="INDIRIZZO_EMAIL"
                  type="email"
                  value={smtpDraft.indirizzoEmail}
                  onChange={(e) => setSmtpDraft((prev) => ({ ...prev, indirizzoEmail: e.target.value }))}
                  disabled={loadingSmtp || savingSmtp}
                />
                <TextField
                  fullWidth
                  label="USER_LOGIN"
                  value={smtpDraft.userLogin}
                  onChange={(e) => setSmtpDraft((prev) => ({ ...prev, userLogin: e.target.value }))}
                  disabled={loadingSmtp || savingSmtp}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="PASSWORD_LOGIN"
                  type="password"
                  value={smtpDraft.passwordLogin}
                  onChange={(e) => setSmtpDraft((prev) => ({ ...prev, passwordLogin: e.target.value }))}
                  disabled={loadingSmtp || savingSmtp}
                  placeholder={
                    smtpDraft.hasSmtpPassword
                      ? t('systemSettings.smtp.passwordPlaceholder', { defaultValue: 'Password saved: leave blank to keep it.' })
                      : ''
                  }
                />
                <TextField
                  fullWidth
                  label="SERVER_SMTP"
                  value={smtpDraft.serverSmtp}
                  onChange={(e) => setSmtpDraft((prev) => ({ ...prev, serverSmtp: e.target.value }))}
                  disabled={loadingSmtp || savingSmtp}
                />
                <TextField
                  label="PORTA_SMTP"
                  type="number"
                  value={smtpDraft.portaSmtp}
                  onChange={(e) => setSmtpDraft((prev) => ({ ...prev, portaSmtp: e.target.value }))}
                  disabled={loadingSmtp || savingSmtp}
                  inputProps={{ min: 1, max: 65535 }}
                  sx={{ minWidth: { xs: '100%', md: 180 } }}
                />
              </Stack>
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="contained" onClick={saveSmtpConfig} disabled={loadingSmtp || savingSmtp}>
                  {savingSmtp ? <CircularProgress size={18} /> : t('common.actions.save', { defaultValue: 'Save' })}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* ===== Sezione: Gestione Tenant ===== */}
        <Card className="rounded-2xl shadow-md" sx={{ mb: 4 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} mb={2}>
              <Typography variant="h5">{t('admin.tenants.title')}</Typography>
              <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenCreateTenant(true)}>
                {t('admin.tenants.actions.newTenant')}
              </Button>
            </Stack>

            {/* Barra di ricerca */}
            <TextField
              fullWidth
              placeholder={t('admin.tenants.search.placeholder')}
              value={tenantSearch}
              onChange={(e) => { setTenantSearch(e.target.value); setTenantPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* ERROR STATE */}
            {tenantsError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={loadTenants}>
                    {t('common.actions.retry')}
                  </Button>
                }
              >
                {tenantsError}
              </Alert>
            )}

            {/* LOADING */}
            {loadingTenants ? (
              <Box py={4} display="flex" justifyContent="center"><CircularProgress /></Box>
            ) : (
              <>
                {/* EMPTY / NO-RESULTS */}
                {!tenantsError && tenants.length === 0 ? (
                  <EmptyState
                    title={t('admin.tenants.empty.noneTitle')}
                    subtitle={t('admin.tenants.empty.noneSubtitle')}
                    action={<Button variant="contained" onClick={() => setOpenCreateTenant(true)}>{t('admin.tenants.actions.newTenant')}</Button>}
                  />
                ) : filteredTenantsAll.length === 0 ? (
                  <EmptyState
                    title={t('admin.tenants.empty.noResultsTitle')}
                    subtitle={t('admin.tenants.empty.noResultsSubtitle')}
                    action={<Button variant="outlined" onClick={() => setTenantSearch('')}>{t('common.actions.clearFilter')}</Button>}
                  />
                ) : (
                  <>
                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ textAlign: 'left' }}>
                            <TableHeader field="tenantId" label={t('admin.tenants.table.tenantId')} sort={tenantSort} onSort={(f) => handleSort(f, tenantSort, setTenantSort)} />
                            <TableHeader field="id" label={t('admin.tenants.table.id')} sort={tenantSort} onSort={(f) => handleSort(f, tenantSort, setTenantSort)} />
                            <TableHeader field="tenantType" label={t('admin.tenants.table.tenantType')} sort={tenantSort} onSort={(f) => handleSort(f, tenantSort, setTenantSort)} />
                            <TableHeader field="isActive" label={t('admin.tenants.table.status')} sort={tenantSort} onSort={(f) => handleSort(f, tenantSort, setTenantSort)} />
                            <TableHeader field="createdAt" label={t('admin.tenants.table.createdAt')} sort={tenantSort} onSort={(f) => handleSort(f, tenantSort, setTenantSort)} />
                            <th style={{ width: 230 }}>{t('admin.tenants.table.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedTenants.map((r) => (
                            <tr key={r.id} style={{ borderTop: '1px solid #eee' }}>
                              <td style={{ fontFeatureSettings: '"tnum"' }}>{r.tenantId}</td>
                              <td style={{ fontFeatureSettings: '"tnum"' }}>{r.id}</td>
                              <td>{r.tenantType || '-'}</td>
                              <td>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Chip
                                    size="small"
                                    label={r.isActive ? t('common.status.active') : t('common.status.inactive')}
                                    color={r.isActive ? 'success' : 'default'}
                                  />
                                  <Switch
                                    checked={!!r.isActive}
                                    onChange={(e) => toggleTenantSwitch(r, e.target.checked)}
                                    inputProps={{
                                      'aria-label': t('admin.tenants.switchAria', {
                                        id: r.tenantId,
                                        state: r.isActive ? t('common.status.inactive') : t('common.status.active')
                                      })
                                    }}
                                  />
                                </Stack>
                              </td>
                              <td>{formatDateTime24(r.createdAt)}</td>
                              <td>
                                <Stack direction="row" spacing={1}>
                                  <Tooltip title={t('admin.integrationKeys.create', { defaultValue: 'Create API key' })}>
                                    <IconButton color="primary" onClick={() => openCreateIntegrationKey(r)}>
                                      <VpnKeyIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('admin.integrationKeys.revoke', { defaultValue: 'Revoke API key' })}>
                                    <IconButton color="warning" onClick={() => openRevokeIntegrationKey(r)}>
                                      <KeyOffIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('common.actions.edit')}>
                                    <IconButton
                                      color="primary"
                                      onClick={() => {
                                        setEditingTenant(r);
                                        setOpenEditTenant(true);
                                      }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('common.actions.delete')}>
                                    <IconButton color="error" onClick={() => openDeleteTenant(r)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>

                    <TablePagination
                      component="div"
                      count={filteredTenantsAll.length}
                      page={tenantPage}
                      onPageChange={(_, newPage) => setTenantPage(newPage)}
                      rowsPerPage={tenantRowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setTenantRowsPerPage(parseInt(e.target.value, 10));
                        setTenantPage(0);
                      }}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      labelRowsPerPage={t('common.pagination.rowsPerPage')}
                      labelDisplayedRows={({ from, to, count }) =>
                        t('common.pagination.displayedRows', { from, to, count })
                      }
                    />
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* ===== Sezione: Gestione Utenti ===== */}
        <Card className="rounded-2xl shadow-md">
          <CardContent>
            <Typography variant="h5" gutterBottom>{t('admin.users.title')}</Typography>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label={t('admin.users.tabs.superadmin')} />
              <Tab label={t('admin.users.tabs.adminByTenant')} />
            </Tabs>

            {/* TAB: SUPERADMIN */}
            {tab === 0 && (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} mb={2}>
                  <TextField
                    placeholder={t('admin.users.search.superadmin')}
                    value={superadminSearch}
                    onChange={(e) => { setSuperadminSearch(e.target.value); setSuperadminPage(0); }}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                    sx={{ minWidth: { xs: 0, sm: 300 }, width: { xs: '100%', sm: 'auto' } }}
                  />
                  <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenCreateUser(true)}>
                    {t('admin.users.buttons.newSuperadmin')}
                  </Button>
                </Stack>

                {superadmins.length === 0 ? (
                  <EmptyState
                    title={t('admin.users.empty.noSuperadminTitle')}
                    subtitle={t('admin.users.empty.noSuperadminSubtitle')}
                    action={<Button variant="contained" onClick={() => setOpenCreateUser(true)}>{t('admin.users.buttons.createSuperadmin')}</Button>}
                  />
                ) : (() => {
                  const filtered = sortData(
                    filterData(superadmins, superadminSearch, ['name', 'surname', 'email']),
                    superadminSort
                  );
                  if (filtered.length === 0) {
                    return (
                      <EmptyState
                        title={t('admin.users.empty.noResultsTitle')}
                        subtitle={t('admin.users.empty.noResultsSubtitleSuperadmin')}
                        action={<Button variant="outlined" onClick={() => setSuperadminSearch('')}>{t('common.actions.clearFilter')}</Button>}
                      />
                    );
                  }
                  const pageData = filtered.slice(superadminPage * superadminRowsPerPage, superadminPage * superadminRowsPerPage + superadminRowsPerPage);
                  return (
                    <>
                      <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ textAlign: 'left' }}>
                              <TableHeader field="name" label={t('admin.users.table.name')} sort={superadminSort} onSort={(f) => handleSort(f, superadminSort, setSuperadminSort)} />
                              <TableHeader field="surname" label={t('admin.users.table.surname')} sort={superadminSort} onSort={(f) => handleSort(f, superadminSort, setSuperadminSort)} />
                              <TableHeader field="email" label={t('admin.users.table.email')} sort={superadminSort} onSort={(f) => handleSort(f, superadminSort, setSuperadminSort)} />
                              <th>{t('admin.users.table.actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pageData.map((u) => (
                              <tr key={u.id} style={{ borderTop: '1px solid #eee' }}>
                                <td>{u.name}</td>
                                <td>{u.surname}</td>
                                <td>{u.email}</td>
                                <td>
                                  <Tooltip title={t('common.actions.delete')}>
                                    <IconButton color="error" onClick={() => deleteSuperadmin(u)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                      <TablePagination
                        component="div"
                        count={filtered.length}
                        page={superadminPage}
                        onPageChange={(_, newPage) => setSuperadminPage(newPage)}
                        rowsPerPage={superadminRowsPerPage}
                        onRowsPerPageChange={(e) => { setSuperadminRowsPerPage(parseInt(e.target.value, 10)); setSuperadminPage(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage={t('common.pagination.rowsPerPage')}
                        labelDisplayedRows={({ from, to, count }) => t('common.pagination.displayedRows', { from, to, count })}
                      />
                    </>
                  );
                })()}

                <CreateUserDialog
                  open={openCreateUser}
                  onClose={() => setOpenCreateUser(false)}
                  onCreate={(form) => { setOpenCreateUser(false); createSuperadmin(form).catch((e) => notify(e.message, 'error')); }}
                  role="superadmin"
                />
              </>
            )}

            {/* TAB: ADMIN PER TENANT */}
            {tab === 1 && (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" mb={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <TextField
                      select
                      label={t('admin.users.dialogs.createUser.fields.tenant')}
                      value={selectedTenant}
                      onChange={(e) => setSelectedTenant(e.target.value)}
                      sx={{ minWidth: { xs: 0, sm: 260 }, width: { xs: '100%', sm: 'auto' } }}
                    >
                      {tenants.map((t) => (
                        <MenuItem key={t.id} value={t.tenantId}>
                          {(t.Id ?? t.id)} ({t.tenantId})
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      placeholder={t('admin.users.search.admin')}
                      value={adminSearch}
                      onChange={(e) => { setAdminSearch(e.target.value); setAdminPage(0); }}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                      sx={{ minWidth: { xs: 0, sm: 250 }, width: { xs: '100%', sm: 'auto' } }}
                    />
                  </Stack>
                  <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenCreateUser(true)}>
                    {t('admin.users.buttons.newAdmin')}
                  </Button>
                </Stack>

                {admins.length === 0 ? (
                  <EmptyState
                    title={t('admin.users.empty.noAdminTitle')}
                    subtitle={t('admin.users.empty.noAdminSubtitle')}
                    action={<Button variant="contained" onClick={() => setOpenCreateUser(true)}>{t('admin.users.buttons.createAdmin')}</Button>}
                  />
                ) : (() => {
                  const filtered = sortData(
                    filterData(admins, adminSearch, ['name', 'surname', 'email']),
                    adminSort
                  );
                  if (filtered.length === 0) {
                    return (
                      <EmptyState
                        title={t('admin.users.empty.noResultsTitle')}
                        subtitle={t('admin.users.empty.noResultsSubtitleAdmin')}
                        action={<Button variant="outlined" onClick={() => setAdminSearch('')}>{t('common.actions.clearFilter')}</Button>}
                      />
                    );
                  }
                  const pageData = filtered.slice(adminPage * adminRowsPerPage, adminPage * adminRowsPerPage + adminRowsPerPage);
                  return (
                    <>
                      <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ textAlign: 'left' }}>
                              <TableHeader field="name" label={t('admin.users.table.name')} sort={adminSort} onSort={(f) => handleSort(f, adminSort, setAdminSort)} />
                              <TableHeader field="surname" label={t('admin.users.table.surname')} sort={adminSort} onSort={(f) => handleSort(f, adminSort, setAdminSort)} />
                              <TableHeader field="email" label={t('admin.users.table.email')} sort={adminSort} onSort={(f) => handleSort(f, adminSort, setAdminSort)} />
                              <th>{t('admin.users.table.actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pageData.map((u) => (
                              <tr key={u.id} style={{ borderTop: '1px solid #eee' }}>
                                <td>{u.name}</td>
                                <td>{u.surname}</td>
                                <td>{u.email}</td>
                                <td>
                                  <Tooltip title={t('common.actions.delete')}>
                                    <IconButton color="error" onClick={() => deleteAdmin(selectedTenant, u)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                      <TablePagination
                        component="div"
                        count={filtered.length}
                        page={adminPage}
                        onPageChange={(_, newPage) => setAdminPage(newPage)}
                        rowsPerPage={adminRowsPerPage}
                        onRowsPerPageChange={(e) => { setAdminRowsPerPage(parseInt(e.target.value, 10)); setAdminPage(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage={t('common.pagination.rowsPerPage')}
                        labelDisplayedRows={({ from, to, count }) => t('common.pagination.displayedRows', { from, to, count })}
                      />
                    </>
                  );
                })()}

                <CreateUserDialog
                  open={openCreateUser}
                  onClose={() => setOpenCreateUser(false)}
                  onCreate={(form) => { setOpenCreateUser(false); createAdmin(form).catch((e) => notify(e.message, 'error')); }}
                  role="admin"
                  selectedTenant={tenants.find(t => t.tenantId === selectedTenant)}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Dialog: create tenant */}
        <CreateTenantDialog
          open={openCreateTenant}
          onClose={() => { setOpenCreateTenant(false) }}
          onCreate={(p) => { setOpenCreateTenant(false); createTenant(p).catch((e) => notify(e.message, 'error')); }}
        />
        <EditTenantDialog
          open={openEditTenant}
          onClose={() =>{
           setOpenEditTenant(false);
           setEditingTenant(null);   
          }}
          tenant={editingTenant}
          onSave={(updated) => {
            updateTenant(updated)
            .then(() => {
            setOpenEditTenant(false);
            setEditingTenant(null);
            updateTenant(updated).catch((e) => notify(e.message, 'error'));
          })
          .catch((e) => notify(e.message, 'error'));
       }}
        />

        <DeleteTenantDialog
          open={deleteDlg.open}
          onClose={closeDeleteTenant}
          onConfirm={confirmDeleteTenant}
          tenantId={deleteDlg.row?.tenantId}
          loading={deleteDlg.loading}
          error={deleteDlg.error}
        />

        <CreateIntegrationKeyDialog
          open={integrationKeyDlg.open}
          tenant={integrationKeyDlg.row}
          loading={integrationKeyDlg.loading}
          error={integrationKeyDlg.error}
          result={integrationKeyDlg.result}
          onClose={closeCreateIntegrationKey}
          onCreate={createIntegrationKey}
          onCopy={copyIntegrationKey}
        />

        <RevokeIntegrationKeyDialog
          open={revokeIntegrationKeyDlg.open}
          tenant={revokeIntegrationKeyDlg.row}
          loading={revokeIntegrationKeyDlg.loading}
          error={revokeIntegrationKeyDlg.error}
          onClose={closeRevokeIntegrationKey}
          onRevoke={revokeIntegrationKey}
        />

        {/* Snackbar */}
        <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })}>
          <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
